import { TableSelector } from "@/common/components/ui/table/tableSelector";
import { PaymentInfo } from "@/types/payment.type";
import { useEffect, useMemo, useState } from "react";
import { renderPaymentContent } from "../../helpers/renderStyle";
import { formatCurrency } from "@/common/helpers/mask.helpers";
import { useFormikContext } from "formik";

type Props = {
  payments?: PaymentInfo[];
};

export function PaymentFormikRenter({ payments }: Props) {
  const formik = useFormikContext();
  const [selectedPayments, setSelectedPayments] = useState<PaymentInfo[]>([]);
  const [allPayments, setAllPayments] = useState<PaymentInfo[]>([]);

  useEffect(() => {
    if (payments && payments.length > 0) {
      setAllPayments(payments);
    }
  }, [payments]);

  const handleToggle = (payment: PaymentInfo) => {
    setSelectedPayments(prev => {
      const isSelected = prev.some(p => p._id === payment._id);
      if (isSelected) {
        return prev.filter(p => p._id !== payment._id);
      } else {
        return [...prev, payment];
      }
    });
  };

  const selectedTotals = useMemo(() => {
    const count = selectedPayments.length;
    const totalValue = selectedPayments.reduce((s, p) => s + (Number(p.value) || 0), 0);
    const ids = selectedPayments.map(p => p._id);
    const externalPaymentsIds = selectedPayments.map(p => p.externalPaymentId);
    return { count, totalValue, ids, externalPaymentsIds };
  }, [selectedPayments]);

  // Update formik values
  useEffect(() => {
    if (formik) {
      formik.setFieldValue('paymentInfo', {
        externalPaymentsIds: selectedTotals.externalPaymentsIds,
        ids: selectedTotals.ids,
        totalValue: selectedTotals.totalValue
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTotals]);
  const allSelected = allPayments.length > 0 && selectedPayments.length === allPayments.length;
  const someSelected = selectedPayments.length > 0 && selectedPayments.length < allPayments.length;

  return (
    <div>
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 mb-4">
        <h2 className="text-xl font-bold text-gray-800 mb-3 flex items-center">
          <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
          </svg>
          Pagamentos do Locatário
        </h2>
        <div className="bg-white/70 backdrop-blur-sm rounded-md p-3 border border-blue-100">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-600">Total selecionado:</span>
            <div className="flex items-center space-x-2">
              <span className="text-lg font-bold text-green-700 bg-green-50 px-2 py-1 rounded-md">
                {formatCurrency(selectedTotals.totalValue)}
              </span>
              {selectedTotals.count > 0 && (
                <span className="bg-blue-100 text-blue-800 text-xs text-center font-medium px-2 py-1 rounded-full">
                  {selectedTotals.count} {selectedTotals.count === 1 ? 'item' : 'itens'}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center mb-2">
        <input
          type="checkbox"
          id="select-all"
          checked={allSelected}
          ref={input => {
            if (input) {
              input.indeterminate = someSelected;
            }
          }}
          onChange={() => {
            if (allSelected) {
              setSelectedPayments([]);
            } else {
              setSelectedPayments(allPayments); // ✅ Usar allPayments em vez de payments
            }
          }}
          className="mr-2 accent-primary"
        />
        <label htmlFor="select-all" className="font-medium text-gray-800 cursor-pointer">
          Selecionar todos
        </label>
      </div>

      <TableSelector
        items={allPayments}
        selectedItems={selectedPayments}
        onItemToggle={handleToggle}
        renderContent={renderPaymentContent}
      />
    </div>
  );
}
